import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { LoadMoreFooter } from '@/components/LoadMoreFooter';
import {
  useFinderAwaitingClassification,
  useProductsLoadMore,
  useProductsStats,
} from '@/features/product/hooks';
import {
  useProductListControls,
  validateProductListSearch,
} from '@/features/product/list-controls';
import {
  ListEmptyState,
  ListErrorState,
  ListLoadingState,
} from '@/features/product/components/table-kit';
import { AwaitingClassificationCallout } from '@/features/predicate/components/AwaitingClassificationCallout';
import { PredicateFinderTable } from '@/features/predicate/components/PredicateFinderTable';
import { PageHero } from '@/components/layout/PageHero';

/**
 * Predicate Finder — list page (table view).
 *
 * Eligibility (device on a Traditional/Abbreviated 510(k) pathway) is
 * decided SERVER-side (`?eligible_for=finder`), so this page paginates the
 * eligible set with true keyset pages instead of downloading the whole
 * roster and filtering client-side. The tiles come from /stats, whose
 * predicate counts are scoped to the same eligible set — every number on
 * this page (tiles + footer) counts exactly what the table lists.
 */
export const Route = createFileRoute('/predicate-finder')({
  validateSearch: validateProductListSearch,
  component: PredicateFinderPage,
});

function PredicateFinderPage() {
  const searchParams = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const {
    searchInput,
    setSearchInput,
    q,
    sort,
    onSort,
    productType,
    onProductType,
    filtering,
  } = useProductListControls(searchParams, navigate);
  const { visible, hasMore, isFetchingMore, loadMore, isLoading, isError, totalCount } =
    useProductsLoadMore(10, { q, sort, productType, eligibleFor: 'finder', dispatchReady: true });
  const statsQuery = useProductsStats();
  // Devices excluded only because a market is still "Not sure yet" — the
  // "Awaiting classification" strip above the table.
  const awaitingQuery = useFinderAwaitingClassification();
  const awaiting = awaitingQuery.data?.items ?? [];
  const stats = {
    total: statsQuery.data?.finder_eligible ?? 0,
    analyzed: statsQuery.data?.analyzed['predicate'] ?? 0,
    running: statsQuery.data?.running['predicate'] ?? 0,
    marketUpdates: statsQuery.data?.market_updates['predicate'] ?? 0,
  };

  return (
    <>
      <PageHero
        title="Existing Products"
        subtitle={<>Pick a registered product to find the existing product it is compared against — a predicate or comparator device, a reference drug, or a reference biologic. Every run is tied to a product — register one first if you haven&apos;t.</>}
      />

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-8 pb-20">
        {isLoading && <ListLoadingState />}
        {isError && <ListErrorState />}
        {!isLoading && !isError && (
          <AwaitingClassificationCallout products={awaiting} />
        )}
        {!isLoading && !isError && visible.length === 0 && !filtering && (
          <ListEmptyState
            title="No products to analyze yet."
            body="Register a product to find the existing product it is compared against."
          >
            <Button variant="brand" asChild>
              <a href="/product-account">Create a product</a>
            </Button>
          </ListEmptyState>
        )}
        {!isLoading && !isError && (visible.length > 0 || filtering) && (
          <>
            <PredicateFinderTable
              products={visible}
              stats={stats}
              search={searchInput}
              onSearch={setSearchInput}
              sort={sort}
              onSort={onSort}
              productType={productType}
              onProductType={onProductType}
            />
            <LoadMoreFooter
              loaded={visible.length}
              total={totalCount}
              hasMore={hasMore}
              isFetching={isFetchingMore}
              onLoadMore={loadMore}
            />
          </>
        )}
      </main>
    </>
  );
}


